import { UserType } from '../api/model/userType';
import { FullTaDetails } from '../api/model/fullTaDetails';
import { BasicTaDetails } from '../api/model/basicTaDetails';
import { TAStatus } from '../api/model/tAStatus';
import { ApprovalContext } from '../api/model/approvalContext';
import {ACQUITTED_STATUSES, CANCEL_FLOW_STATUSES, PrepaidIndicator} from './app.types';

/**
 * Returns the display-name suffix marking a casual user.
 * Yields '[C]' for casual users and an empty string otherwise, so the result
 * can be safely concatenated onto a user's display name.
 * e.g. `user.name + getUserTypeSuffix(user)` → "Jane Doe[C]" | "Jane Doe"
 */
export function getUserTypeSuffix(user?: { userType?: UserType | null } | null): string {
  return user?.userType === UserType.Casual ? '[C]' : '';
}

/**
 * Resolves the UserTravel id to anchor an itinerary fetch to.
 *
 * A TA is legitimate as long as it has a booking officer OR a primary traveller, so the itinerary
 * must never hang off the primary traveller alone: a delegate TA whose travellers are all casual
 * has no primary traveller at all, because a casual can never hold that role.
 *
 * Preference order is the viewer's own travel portion, then the primary traveller's, then the
 * first traveller's. Every itinerary endpoint keys on the UserTravel id rather than the primary
 * traveller, so any traveller on the TA resolves to a fetchable itinerary.
 */
export function resolveItineraryTravellerId(
  taSubmission?: FullTaDetails | null,
  sessionUserId?: string | null,
): string | undefined {
  const travellers = taSubmission?.travellers ?? [];
  const ownTravel = !!sessionUserId
    ? travellers.find(traveller => traveller.details?.id === sessionUserId)
    : undefined;

  return ownTravel?.travellerId
    ?? taSubmission?.primaryTraveller?.travellerId
    ?? travellers[0]?.travellerId
    ?? undefined;
}

/** The expense figures shared by SubmissionExpenseResult, BasicTaDetails and FullTaDetails. */
export type TaValueFigures = Pick<BasicTaDetails, 'originalValue' | 'currentValue' | 'prePaidValue' | 'currentFinanceValue'>;

/**
 * Current value less the baseline. A pre-payment never covers day trips (paid separately), so it is
 * compared against the current finance value. Mirrors TAStore.IsOverpaid.
 */
export function taValueChange(values: TaValueFigures): number {
  const current = values.currentValue ?? 0;
  return values.prePaidValue != null
    ? (values.currentFinanceValue ?? current) - values.prePaidValue
    : current - (values.originalValue ?? 0);
}

/**
 * Pre-payment, day-trip and finalisation figures. The pre-payment is the processed one, else what
 * processing would snapshot; prepaid TAs deduct it from the finalisation payment.
 */
export function taPaymentComponents(values: TaValueFigures & Pick<BasicTaDetails, 'isPrePaid'>) {
  const current = values.currentValue ?? 0;
  const finance = values.currentFinanceValue ?? current;
  const prepayment = values.prePaidValue ?? finance;
  return {
    prepayment,
    dayTrip: current - finance,
    finalisationDue: Math.max(0, finance - (values.isPrePaid ? prepayment : 0)),
  };
}

/** Pre-payment in excess of what the TA is now worth; 0 when not prepaid (processed) or not overpaid. */
export function taOverpaidAmount(values: TaValueFigures): number {
  return values.prePaidValue != null ? Math.max(0, -taValueChange(values)) : 0;
}

/** More was paid (or estimated) than the TA is now worth - see taValueChange. */
export function isTaOverpaid(values: TaValueFigures): boolean {
  return taValueChange(values) < 0;
}

/**
 * Classifies a TA for the prepaid indicator column: not prepaid at all, prepaid, or prepaid with
 * money to come back - either acquitted for less than was paid (see isTaOverpaid), or cancelled after the
 * prepayment had been paid out. Mirrors the overpayment test the finalise page uses, with the extra
 * guard that the TA has reached acquittal.
 */
export function prepaidIndicator(taDetails?: BasicTaDetails | null): PrepaidIndicator {
  if (!taDetails?.isPrePaid) {
    return 'none';
  }

  // The cancellation flow and the Cancelled TA it leaves behind. Approving the cancellation zeroes
  // the itinerary, so the whole prepayment ends up outstanding - and it stays outstanding once the
  // TA reaches Cancelled, which is where it spends the rest of its life. Flagged from the cancel
  // *request* rather than the approval: finance wants the warning while the recovery is still
  // pending, and the shortfall test below cannot see it (a PendingCancelApproval TA still holds its
  // full value, so there is no shortfall yet).
  //
  // prePaymentProcessed is what separates the two routes to Cancelled: the immediate-cancel path
  // lands there too, but nothing was ever paid out and its values are left intact, so there is
  // nothing to recover.
  if (CANCEL_FLOW_STATUSES.includes(taDetails.status as TAStatus)
    || taDetails.status === TAStatus.Cancelled) {
    return taDetails.prePaymentProcessed ? 'overpaid' : 'prepaid';
  }

  const acquitted = ACQUITTED_STATUSES.includes(taDetails.status as TAStatus);
  return acquitted && isTaOverpaid(taDetails) ? 'overpaid' : 'prepaid';
}

type ItineraryDayFlags = { isWeekend?: boolean; isPublicHoliday?: boolean };

const isDayOff = (item?: ItineraryDayFlags | null) => !!(item?.isPublicHoliday || item?.isWeekend);

/**
 * Row classes for itinerary row `index` of `items` (the array the table iterates), from the server's weekend/public
 * holiday flags: tints the row (styles.css) and draws a boundary line under each row where off-day status changes.
 */
export function itineraryDayRowClass(items: readonly (ItineraryDayFlags | null | undefined)[], index: number): string {
  const off = isDayOff(items[index]);
  const boundary = off !== isDayOff(items[index + 1]);
  return [off ? 'itinerary-day-off' : '', boundary ? 'itinerary-day-boundary' : ''].join(' ').trim();
}

/**
 * One run of a string, flagged as matching the active filter or not.
 * Rendered as discrete spans so highlighting never needs innerHTML.
 */
export interface HighlightSegment {
  text: string;
  matched: boolean;
}

/**
 * Splits a filter string into lowercased search tokens on whitespace.
 * e.g. `'  Job  Failed '` → `['job', 'failed']`
 */
export function filterTokens(filter: string): string[] {
  return filter.trim().toLowerCase().split(/\s+/).filter(token => token.length > 0);
}

/**
 * True when every token appears somewhere in the haystack, in any order.
 * An empty token list matches everything, so an empty filter is a no-op.
 */
export function matchesTokens(haystack: string, tokens: string[]): boolean {
  if (tokens.length === 0) {
    return true;
  }
  const lower = haystack.toLowerCase();
  return tokens.every(token => lower.includes(token));
}

/**
 * Splits text into alternating matched/unmatched runs for the given tokens.
 *
 * Works by masking every matched character position and then coalescing adjacent
 * positions with the same state, so overlapping and repeated tokens each highlight
 * once rather than nesting or double-wrapping.
 */
export function highlightSegments(text: string, tokens: string[]): HighlightSegment[] {
  if (!text) {
    return [];
  }
  if (tokens.length === 0) {
    return [{text, matched: false}];
  }

  const lower = text.toLowerCase();
  const mask = new Array<boolean>(text.length).fill(false);
  for (const token of tokens) {
    let at = lower.indexOf(token);
    while (at !== -1) {
      for (let i = at; i < at + token.length; i++) {
        mask[i] = true;
      }
      at = lower.indexOf(token, at + 1);
    }
  }

  const segments: HighlightSegment[] = [];
  let start = 0;
  for (let i = 1; i <= text.length; i++) {
    if (i === text.length || mask[i] !== mask[start]) {
      segments.push({text: text.slice(start, i), matched: mask[start]});
      start = i;
    }
  }
  return segments;
}

/** The server raises a DayTripPayment approval for Compliance when any traveller claims the $45. */
// The day trip portion is paid by Compliance, separately from Finance's finalisation payment.
export function hasDayTripPayment(taDetails?: BasicTaDetails | FullTaDetails | null): boolean {
  return (taDetails?.approvals ?? []).some(approval => approval.actionContext === ApprovalContext.DayTripPayment);
}

export function hasPendingDayTripPayment(taDetails?: BasicTaDetails | FullTaDetails | null): boolean {
  return (taDetails?.approvals ?? []).some(approval =>
    approval.actionContext === ApprovalContext.DayTripPayment && approval.status !== 'Actioned');
}
